import { FinishReason } from "@perceptron/api/apiContracts/chatCompletions";
import { strings } from "@perceptron/constants";
import { generateUUID } from "@perceptron/utils/crypto";
import { MediaKind } from "@perceptron/utils/types";
import { toast } from "sonner";

import { TEMPLATE_REGISTRY, type ComposeTemplate } from "@assets/templates";
import { getPendingFixture } from "@demo/hooks/useDevTestData/pendingFixture";
import { trackModelRun } from "@demo/libs/analytics/utils";
import { demoModelApi, type StreamRunRequest } from "@demo/libs/api/demoApiClient";
import { saveComposeRunPreferences } from "@demo/libs/run-preferences";
import { createOutputRunContext } from "@demo/libs/tracking-recovery";
import { RunOutputPart, RunUsagePart } from "@demo/server/api/contracts/run";
import type { ToolCallEvent } from "@demo/server/api/contracts/toolCall";
import { useEditorRefStore } from "@demo/store/editor-ref";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState, useModelStore } from "@demo/store/model";
import useSessionStore from "@demo/store/session";
import { useToolCallStore } from "@demo/store/tool-calls";
import { Role } from "@demo/types/base";
import { getNumberOfAnnotations, getNumberOfImages, getPromptCharacterCount } from "@demo/utils/document";
import { DocumentContentProcessor } from "@demo/utils/document-content-processor";

/**
 * Sends user prompt content to the model via REST SSE streaming
 */
export const sendToModel = async () => {
  const {
    setAgentState,
    setSendToModelAbortController,
    handleReasoningToken,
    handleContentToken,
    resetStreamingState,
    clearAgentOutputAndSetAgentState,
    outputFormat,
    enableReasoning,
    enableAudioInVideo,
    enabledTools,
    temperature,
  } = useModelStore.getState();
  const { userEditorRef } = useEditorRefStore.getState();
  const selectedModel = useModelStore.getState().selectedModel;
  const customModelName = useModelStore.getState().customModelName;

  const userJson = userEditorRef?.editor?.getJSON();
  const processor = new DocumentContentProcessor();

  if (!userJson) {
    throw new Error("User editor content is undefined");
  }

  const distinctMediaKinds = processor.extractDistinctMediaKinds(userJson);

  const canModelSupportMedia = [...distinctMediaKinds].every((kind) =>
    selectedModel?.capabilities.media.includes(kind),
  );

  if (!canModelSupportMedia) {
    toast.error(strings.model.errors.modelDoesNotSupportMedia);
    return;
  }

  clearAgentOutputAndSetAgentState(AgentState.EVALUATING);

  const previousController = useModelStore.getState().sendToModelAbortController;
  if (previousController) {
    previousController.abort();
  }

  // Process TipTap JSON to structured content
  const userContent = processor.processJsonToContent(userJson, Role.USER);

  const sessionUuid = useSessionStore.getState().sessionUuid;
  // Every asset in the prompt, in document order — the output carousel indexes
  // into this list by an annotation's `asset_idx`.
  useMediaCarouselStore.getState().setMediaInAgentCarousel(processor.extractMediaFromDocuments([userJson]));

  const controller = new AbortController();
  setSendToModelAbortController(controller);

  const runUuid = generateUUID();

  // Stream metrics
  let firstTokenTimestamp: string | undefined;
  let lastTokenTimestamp: string | undefined;
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let finishReason: FinishReason | undefined;
  let hasStreamError = false;

  const template = TEMPLATE_REGISTRY.templates.find(
    (template): template is ComposeTemplate =>
      template.mode === "compose" && JSON.stringify(template.request.user) === JSON.stringify(userContent),
  );

  const handleError = (error: { message: string; code: string | undefined }) => {
    hasStreamError = true;
    toast.error(error.message);
    controller.abort();
    clearAgentOutputAndSetAgentState(AgentState.IDLE);
    setSendToModelAbortController(null);
    trackModelRun.fail({
      runId: runUuid,
      template: template,
      error: error.message,
      errorCode: error.code,
    });
  };

  try {
    let receivedFirstToken = false;
    const onFirstSignal = () => {
      receivedFirstToken = true;
      firstTokenTimestamp = new Date().toISOString();
      resetStreamingState({ preserveOutputRunContext: true });
    };

    const userPromptCharacterCount = getPromptCharacterCount(userContent);
    const numberOfImages = getNumberOfImages(userContent);
    const numberOfAnnotations = getNumberOfAnnotations(userContent);

    // Track run start
    trackModelRun.start({
      runId: runUuid,
      template: template,
      promptCharacterCount: userPromptCharacterCount,
      numberOfImages: numberOfImages,
      userAnnotationsCount: numberOfAnnotations,
      outputFormat: outputFormat.value,
    });

    const model: typeof selectedModel = !!customModelName
      ? {
          ...selectedModel,
          name: customModelName as (typeof selectedModel)["name"],
          provider: "nebius",
          // A custom endpoint is not the catalogued Mk1.5 model, even when
          // the user entered it while that model was selected.
          defaultTemperature: undefined,
        }
      : selectedModel;

    // Stream the model response using REST SSE
    const streamRequest: StreamRunRequest = {
      model: model,
      sessionUuid,
      runUuid,
      content: userContent,
      outputFormatSchema: outputFormat.value,
      enableReasoning,
      // Audio is only asked of a model that hears it, and only for a run with a video.
      ...(enableAudioInVideo === undefined ||
      !model.capabilities.audioInVideo ||
      !distinctMediaKinds.has(MediaKind.Video)
        ? {}
        : { enableAudioInVideo }),
      tools: enabledTools,
      // Dev-only: the server replays this dev fixture instead of calling the model or the harness.
      fixture: getPendingFixture()?.key,
      fixtureSpeed: getPendingFixture()?.speed,
      temperature:
        temperature ??
        template?.request.temperature ??
        model.defaultTemperature ??
        (model.provider === "nebius" ? 0 : undefined),
    };
    useModelStore.getState().setLastRequest(streamRequest);
    useModelStore
      .getState()
      .setOutputRunContext(createOutputRunContext(streamRequest, customModelName || model.publicName));

    saveComposeRunPreferences({
      modelName: customModelName ? "custom-model" : selectedModel.name,
      customModelName,
      outputFormat: outputFormat.value,
      temperature,
      enableReasoning,
      tools: enabledTools,
    });
    await demoModelApi.streamRun(
      streamRequest,
      (part: RunOutputPart) => {
        const reasoning = part.reasoning_content;
        const content = part.content;

        // Fire TTFT on first token of ANY type (reasoning or content)
        if (!receivedFirstToken && (reasoning || content)) {
          onFirstSignal();
        }

        // Update timestamp for any token
        if (reasoning || content) {
          lastTokenTimestamp = new Date().toISOString();
        }

        // Route tokens to appropriate handlers
        if (reasoning) {
          handleReasoningToken(reasoning);
        }

        if (content) {
          handleContentToken(content);
        }

        // End reasoning and store finish reason when stream finishes
        if (part.finish_reason) {
          finishReason = part.finish_reason;
          useModelStore.getState().handleFinishReason(part.finish_reason);
        }
      },
      (_) => {
        // Handle SSE error events
        handleError({
          message: "Error occurred while running the model.",
          code: "STREAM_ERROR",
        });
      },
      (usage: RunUsagePart) => {
        // Handle usage data
        inputTokens = usage.inputTokens;
        outputTokens = usage.outputTokens;
      },
      controller.signal,
      (event: ToolCallEvent) => {
        // A call that begins before any text has streamed still flips the UI out of "evaluating".
        if (!receivedFirstToken) onFirstSignal();
        lastTokenTimestamp = new Date().toISOString();
        const { getReasoningOutput, getOutput } = useModelStore.getState();
        const reasoningLength = getReasoningOutput().length;
        useToolCallStore.getState().applyEvent(event, {
          reasoningAnchor: reasoningLength,
          outputLength: reasoningLength + getOutput().length,
        });
      },
    );

    // Stream completed successfully
    setAgentState(AgentState.IDLE);
    setSendToModelAbortController(null);

    // No token of any kind arrived, so resetStreamingState never fired
    if (!hasStreamError && !receivedFirstToken) {
      useModelStore.getState().setIsEmptyResponse(true);
    }

    if (!hasStreamError) {
      const duration =
        !!lastTokenTimestamp && !!firstTokenTimestamp
          ? new Date(lastTokenTimestamp).getTime() - new Date(firstTokenTimestamp).getTime()
          : 0;

      trackModelRun.complete({
        runId: runUuid,
        template: template,
        totalInputTokens: inputTokens,
        totalOutputTokens: outputTokens,
        timeToFirstToken: firstTokenTimestamp ? new Date(firstTokenTimestamp).getTime() : undefined,
        timeToLastToken: lastTokenTimestamp ? new Date(lastTokenTimestamp).getTime() : undefined,
        duration: duration,
        outputFormat: outputFormat.value,
        finishReason: finishReason,
      });
    }
  } catch (error: unknown) {
    setAgentState(AgentState.IDLE);
    setSendToModelAbortController(null);

    // Don't treat user-initiated abort as an error
    const isAbortError = error instanceof DOMException && error.name === "AbortError";
    if (isAbortError) {
      const timeBeforeCancellation = firstTokenTimestamp
        ? Date.now() - new Date(firstTokenTimestamp).getTime()
        : undefined;

      trackModelRun.cancel({
        runId: runUuid,
        template: template,
        partialOutputTokens: outputTokens,
        timeBeforeCancellation: timeBeforeCancellation,
        didEmitFirstToken: !!firstTokenTimestamp,
      });
      return;
    }

    if (!hasStreamError) {
      // we already handle this in the stream event
      if (error instanceof Error) {
        handleError({
          message: error.message,
          code: undefined,
        });
      } else {
        handleError({
          message: "Unknown SSE error",
          code: "UNKNOWN_SSE_ERROR",
        });
      }
    }
  }
};
